import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Divider, Form, Input, Popconfirm, Table } from 'antd';
import { get, set } from 'lodash';
import { ColumnType, TableProps } from 'antd/es/table';
import { FormInstance, Rule } from 'antd/es/form';
import { TableRowSelection } from 'antd/es/table/interface';
import { EditOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';

import { isEmptyObject } from '@/utils';
import IntlContext from '@/components/Intl/IntlContext';
import InputMoney from '../InputMoney';
import { isDecimalsFn } from '../InputMoney/validator';
import style from './index.module.scss';

const EditableContext = React.createContext<FormInstance<any> | null>(null);

type CellEditMode = '' | 'popConfirm';
type CellInputType = '' | 'number';

export interface IshouldEditCellProps {
  editable?: boolean; // 针对每行是否可编辑
  rules?: Rule[];
  cellPrefix?: string;
  cellDisplay?: boolean;
  cellEditMode?: CellEditMode; // 编辑框是否是pop模式
  cellInputType?: CellInputType; // 编辑框类型
  cellClassName?: string;
  cellStyle?: React.CSSProperties;
  cellInputItemStyle?: React.CSSProperties;
}

interface EditableRowProps {
  index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false} key={index} autoComplete="off">
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps extends IshouldEditCellProps {
  title?: string;
  dataIndex: any;
  children: React.ReactNode;
  record: any;
  handleSave: (record: any, dataIndex: any, isSaveFailed?: boolean) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editable,
  rules,
  cellPrefix,
  cellDisplay = true,
  cellEditMode,
  cellInputType,
  cellClassName,
  cellStyle,
  cellInputItemStyle,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  const intlCtx = useContext(IntlContext);
  const inputPrefix = cellPrefix ?? intlCtx.TenantBaseConfig.CurrencySymbol;

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus({ cursor: 'end' });
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    if (!editing) {
      form.setFieldsValue(set(record, dataIndex, get(record, dataIndex)));
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave(set(record, dataIndex, get(values, dataIndex)), dataIndex, false);
    } catch (errInfo) {
      const allValues = await form.getFieldsValue();
      handleSave(set(record, dataIndex, get(allValues, dataIndex)), dataIndex, true);
      // console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  /* 过滤出券按钮 */
  let couponNode = null;
  React.Children.map(children, (child: any) => {
    if (child && child.props && child.props.children) {
      React.Children.map(child.props.children, (subChild: any) => {
        if (subChild?.props?.className && subChild?.props?.className?.includes('coupon')) {
          couponNode = subChild;
        }
      });
    }
  });

  const { maxDecimalsDigits, numberReg } = isDecimalsFn() || {};

  if (editable) {
    if (cellEditMode === 'popConfirm') {
      childNode = (
        <>
          {children}
          <Popconfirm
            title={
              <>
                <div className="flex-center" style={{ alignItems: 'flex-start' }}>
                  <Form.Item name={dataIndex} rules={rules}>
                    <Input.TextArea ref={inputRef} onPressEnter={save} autoSize />
                  </Form.Item>
                  <Form.Item>
                    <Button type="link" onClick={save}>
                      {intl.get('cmp_editable_cell_table_save_btn')}
                    </Button>
                    <Divider type="vertical" />
                    <Button type="link" onClick={toggleEdit}>
                      {intl.get('app_confirm_cancel_btn')}
                    </Button>
                  </Form.Item>
                </div>
              </>
            }
            icon=""
            placement="bottomRight"
            overlayClassName="editPopConfirm"
            visible={editing}
          >
            <span onClick={toggleEdit} style={{ cursor: 'pointer' }}>
              <EditOutlined style={{ marginLeft: '8px' }} />
            </span>
          </Popconfirm>
        </>
      );
    } else {
      childNode = editing ? (
        <>
          <div
            className={`${cellInputType === 'number' ? 'flex flex-end' : ''}`}
            style={{ ...cellStyle }}
          >
            <Form.Item
              name={dataIndex}
              rules={rules}
              style={{ width: 120, marginBottom: 0, ...cellInputItemStyle }}
            >
              <InputMoney prefix={inputPrefix} ref={inputRef} onBlur={save} />
            </Form.Item>
            {/* 券按钮 */}
            {couponNode}
          </div>
        </>
      ) : (
        <div
          className={`${cellInputType === 'number' ? 'flex flex-end' : ''}`}
          style={{
            minWidth: 120,
            textAlign: cellInputType === 'number' ? 'right' : 'left',
            ...cellStyle,
          }}
        >
          <div
            className="editable-cell-value-wrap"
            style={{ minWidth: 75, ...cellInputItemStyle }}
            onClick={toggleEdit}
          >
            {get(record, dataIndex) !== undefined &&
              get(record, dataIndex) !== '' &&
              get(record, dataIndex) !== null && (
                <>
                  <span style={{ marginRight: '4px' }}>{inputPrefix}</span>
                  <span>
                    {numberReg?.test(get(record, dataIndex))
                      ? Number(get(record, dataIndex))?.toFixed(maxDecimalsDigits)
                      : get(record, dataIndex)}
                  </span>
                </>
              )}
          </div>
          {couponNode}
        </div>
      );
    }
  } else {
    // 不可编辑单元格
    childNode = (
      <>
        <div
          className={`${cellInputType === 'number' ? 'flex flex-end' : ''}`}
          style={{ minWidth: 120, ...cellStyle }}
        >
          {cellInputType === 'number' ? (
            <>
              <div style={{ minWidth: 75, ...cellInputItemStyle }}>
                {get(record, dataIndex) !== undefined &&
                  get(record, dataIndex) !== '' &&
                  get(record, dataIndex) !== null && (
                    <>
                      <span style={{ marginRight: '4px' }}>{inputPrefix}</span>
                      <span>
                        {numberReg?.test(get(record, dataIndex))
                          ? Number(get(record, dataIndex))?.toFixed(maxDecimalsDigits)
                          : get(record, dataIndex)}
                      </span>
                    </>
                  )}
              </div>
              {couponNode}
            </>
          ) : (
            // 原始dataIndex内容
            children
          )}
        </div>
      </>
    );
  }
  return (
    <td {...restProps}>
      <div className={cellClassName}>{cellDisplay ? childNode : null}</div>
    </td>
  );
};

// type WhatIsDataType<T> = T extends Array<infer V> ? V : never;

// type ExtraColumns = { editable?: boolean; dataIndex: string; title: string };
// export type MyColumns = ColumnsType<any> & ExtraColumns;

export interface IcustomColumnType<T> extends ColumnType<T> {
  editable?: boolean; // 整列的编辑
  rules?: Rule[]; // 整列的规则，如需区分在shouldEditCell中定义
  shouldEditCell?: (record: T) => IshouldEditCellProps;
  onCell?: (record: T) => any;
}

export interface EditableTableProps<T> extends Omit<TableProps<any>, 'columns' | 'dataSource'> {
  showRowSelection?: boolean;
  columns: Array<IcustomColumnType<T>>;
  dataSource: Array<T>;
  /**
   * value: 所有数据, index: 修改所在行, dataIndex: 行所在列（键值）
   */
  onSaveCellChange?: (
    values: Array<T>,
    index: number,
    dataIndex: string | string[],
    isSaveFailed?: boolean,
  ) => void;
  rowSelectionProps?: TableRowSelection<any>;
  onCheckBoxchange?: (selectedRowKeys: Array<any>, selectedRows: Array<T>) => void;
}

export default class EditCellTable<T> extends React.PureComponent<
  EditableTableProps<T & { id: number | string }>,
  any
> {
  handleSave = (row, dataIndex, isSaveFailed) => {
    const { dataSource, onSaveCellChange } = this.props;
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.id === item.id);
    const item = newData[index];
    // 根据路径嵌套更新复杂对象值
    const result = set(item, dataIndex, get(row, dataIndex));
    newData.splice(index, 1, {
      ...item,
      ...result,
    });
    onSaveCellChange?.(newData, index, dataIndex, isSaveFailed);
  };

  render() {
    const {
      showRowSelection = false,
      columns,
      dataSource,
      rowSelectionProps,
      onCheckBoxchange,
      ...rest
    } = this.props;

    const rowSelection = {
      fixed: true,
      onChange: (selectedRowKeys, selectedRows) => {
        // console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
        onCheckBoxchange?.(selectedRowKeys, selectedRows);
      },
      // onSelect: (record, selected, selectedRows) => {
      //   console.log(record, selected, selectedRows);
      // },
      // onSelectAll: (selected, selectedRows, changeRows) => {
      //   console.log(selected, selectedRows, changeRows);
      // },
      ...rowSelectionProps,
    };

    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };
    const cols = columns?.map((col = {}) => {
      if (!col.editable && !('shouldEditCell' in col)) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => {
          let cellSettingObj: any = {};
          if ('shouldEditCell' in col) {
            cellSettingObj = col.shouldEditCell(record) || {};
          }
          return {
            record,
            editable: !isEmptyObject(cellSettingObj) ? cellSettingObj.editable : col.editable,
            rules: col.rules,
            dataIndex: col.dataIndex,
            title: col.title,
            ...cellSettingObj,
            handleSave: this.handleSave,
          };
        },
      };
    });
    return (
      <div className={style.container}>
        {showRowSelection ? (
          <Table
            components={components}
            rowClassName={() => 'show-selection-editable-row'}
            bordered={false}
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            dataSource={dataSource}
            columns={cols}
            {...rest}
          />
        ) : (
          <Table
            components={components}
            rowClassName={() => 'editable-row'}
            bordered={false}
            rowKey={(record) => record.id}
            dataSource={dataSource}
            columns={cols}
            {...rest}
          />
        )}
      </div>
    );
  }
}
